import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import styled from "styled-components/macro";
import { getAppConfig, getLanguage, getSessionIdSafe } from "../../store/sagas/selectors";
import { configuration } from "../../api/client/SdsWebApiClient";
import { useRemoteControlEnabled } from "../../hooks/useRemoteControlEnabled";

// `language` in Redux is mixed-case ("EN", "SV", "DE", "en", "sv", …) depending
// on the source. The remote-control page expects an uppercase tag matching the
// keys in `appConfig.translations.<LANG>`, so normalize once here.
const normalizeLang = (lang: unknown): string => (typeof lang === "string" ? lang.toUpperCase() : "");

const QrCodeContainer = styled.div<{ $embedded?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    background: white;
    padding: ${({ $embedded }) => ($embedded ? "6px" : "8px")};
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    ${({ $embedded }) =>
        !$embedded &&
        `
        position: absolute;
        top: 20px;
        left: 20px;
        z-index: 1000;
    `}
`;

const QrCodeImage = styled.img`
    width: 120px;
    height: 120px;
    display: block;
`;

const ScanToStartLabel = styled.div`
    font-family: ${props => props.theme.fontFamily};
    font-size: 14px;
    color: #333;
    font-weight: 700;
    text-align: center;
    margin-top: -8px;
`;

const QrCodePlaceholder = styled.div`
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    color: #999;
    font-size: 12px;
    text-align: center;
    padding: 10px;
`;

const TestQrCodesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px solid #e0e0e0;
`;

const TestQrCodeItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`;

const TestQrCodeLabel = styled.div`
    font-size: 11px;
    color: #666;
    font-weight: 500;
    text-align: center;
`;

const TestQrCodeImage = styled.img`
    width: 100px;
    height: 100px;
    display: block;
`;

interface TestQrCode {
    label: string;
    retailerId: string;
}

const TEST_QR_CODES: TestQrCode[] = [
    { label: "Tile", retailerId: "SBASSORTP006427" },
    { label: "Wallpaper", retailerId: "BR4511" },
    { label: "Cabinet", retailerId: "__ws-03" },
    { label: "Color for cabinet", retailerId: "__ws-03-bl" },
];

interface RemoteControlQrCodeProps {
    embedded?: boolean;
}

export const RemoteControlQrCode: React.FC<RemoteControlQrCodeProps> = ({ embedded }) => {
    const sessionId = useSelector(getSessionIdSafe);
    const appConfig = useSelector(getAppConfig);
    const language = useSelector(getLanguage);
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const showRemoteControlLink = useRemoteControlEnabled();
    const scanToStartText = get(appConfig, `translations.${language}.scanToStart`);

    const urlParams = new URLSearchParams(window.location.search);
    const ShowRemoteTests = urlParams.get("show_remote_tests")?.toLowerCase() === "true";

    const basePath = configuration.basePath || window.location.origin;

    useEffect(() => {
        if (!showRemoteControlLink || !sessionId) {
            setQrCodeUrl(null);
            return;
        }

        // QR carries only session_id + lang to keep the encoded URL small
        // enough for reliable scanning. The mobile page fetches the localized
        // strings itself from /defaultAppConfig.json (served as a public static
        // asset) using this lang as the key.
        const params = new URLSearchParams();
        params.set("session_id", sessionId);
        const langCode = normalizeLang(language);
        if (langCode) {
            params.set("lang", langCode);
        }

        setQrCodeUrl(`${basePath}/remote_control/qr?${params.toString()}`);
        setError(null);
    }, [showRemoteControlLink, sessionId, basePath, language]);

    if (!showRemoteControlLink) {
        return null;
    }

    return (
        <QrCodeContainer $embedded={embedded}>
            {error ? (
                <QrCodePlaceholder>Error loading QR code</QrCodePlaceholder>
            ) : !qrCodeUrl ? (
                <QrCodePlaceholder>Loading...</QrCodePlaceholder>
            ) : (
                <QrCodeImage
                    src={qrCodeUrl}
                    alt="Remote Control QR Code"
                    onError={() => setError("Failed to load QR code")}
                />
            )}

            {scanToStartText && <ScanToStartLabel>{scanToStartText}</ScanToStartLabel>}

            {ShowRemoteTests && (
                <TestQrCodesContainer>
                    {TEST_QR_CODES.map(testQrCode => (
                        <TestQrCodeItem key={testQrCode.retailerId}>
                            <TestQrCodeLabel>{testQrCode.label}</TestQrCodeLabel>
                            <TestQrCodeImage
                                src={`${basePath}/generate_product_qr?retailer_id=${encodeURIComponent(
                                    testQrCode.retailerId
                                )}`}
                                alt={`Test QR Code: ${testQrCode.label}`}
                            />
                        </TestQrCodeItem>
                    ))}
                </TestQrCodesContainer>
            )}
        </QrCodeContainer>
    );
};
